// Threaded chat sessions for one user, persisted to sessionStorage: closing the
// drawer or navigating inside the tab never discards a thread, and the blob dies with
// the tab. Versioned; a stale version is discarded rather than migrated.

import { ChatContext, contextPhrase } from './pageContext';

export type StoredMessage = {
  role: 'user' | 'assistant';
  text: string;
  runId?: string;
  interrupted?: boolean;
  feedback?: 'up' | 'down';
  // Reserved for v2 uploads; nothing writes it today.
  attachments?: unknown[];
};

export type ChatThread = {
  id: string;
  // Identifies the whole conversation on the wire (agent_runs.conversation_id); one
  // per chat, stable across its turns, so the reflection sweep can group them.
  conversationId: string;
  context: ChatContext;
  messages: StoredMessage[];
  draft: string;
  createdAt: number;
  title: string;
};

export type ThreadState = {
  v: 3;
  activeId: string | null;
  threads: Record<string, ChatThread>;
};

export const MAX_MESSAGES = 40;
export const MAX_CHARS = 256 * 1024;
export const MAX_THREADS = 10;

const VERSION = 3;
const keyFor = (userKey: string): string => `agentChat:v${VERSION}:${userKey}`;

// Identity of a pin. Two Ask-AI clicks on the same campaign reactivate one thread
// instead of stacking duplicates.
export const pinKey = (context: ChatContext): string => {
  const ids = Object.entries(context.entities)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}=${value}`)
    .join(',');
  return `${context.pageType}|${ids}`;
};

export const emptyState = (): ThreadState => ({ v: VERSION, activeId: null, threads: {} });

export const newThread = (context: ChatContext, createdAt: number = Date.now()): ChatThread => ({
  id: `${pinKey(context)}#${createdAt}`,
  conversationId: crypto.randomUUID(),
  context,
  messages: [],
  draft: '',
  createdAt,
  title: contextPhrase(context) ?? (context.pageType === 'generic' ? 'New chat' : context.pageType.replace('_', ' ')),
});

export const threadsByRecency = (state: ThreadState): ChatThread[] =>
  Object.values(state.threads).sort((a, b) => b.createdAt - a.createdAt);

export const findThreadByPin = (state: ThreadState, context: ChatContext): ChatThread | null => {
  const key = pinKey(context);
  return threadsByRecency(state).find((thread) => pinKey(thread.context) === key) ?? null;
};

// Two threads created in the same millisecond would otherwise share an id.
export const freshThread = (state: ThreadState, context: ChatContext): ChatThread => {
  let thread = newThread(context);
  while (state.threads[thread.id]) {
    thread = newThread(context, thread.createdAt + 1);
  }
  return thread;
};

const rebuild = (threads: ChatThread[], activeId: string | null): ThreadState => ({
  v: VERSION,
  activeId: threads.some((t) => t.id === activeId) ? activeId : (threads[0]?.id ?? null),
  threads: Object.fromEntries(threads.map((t) => [t.id, t])),
});

// Caps, newest-first: at most MAX_THREADS threads, MAX_MESSAGES turns each, and the
// whole blob under MAX_CHARS — shedding the oldest threads, then the oldest turns of
// the one thread that is left.
export const prune = (state: ThreadState): ThreadState => {
  let kept = threadsByRecency(state)
    .slice(0, MAX_THREADS)
    .map((t) => (t.messages.length > MAX_MESSAGES ? { ...t, messages: t.messages.slice(-MAX_MESSAGES) } : t));
  let out = rebuild(kept, state.activeId);

  while (kept.length > 1 && JSON.stringify(out).length > MAX_CHARS) {
    kept = kept.slice(0, -1);
    out = rebuild(kept, state.activeId);
  }
  while (kept.length === 1 && kept[0].messages.length > 1 && JSON.stringify(out).length > MAX_CHARS) {
    kept = [{ ...kept[0], messages: kept[0].messages.slice(1) }];
    out = rebuild(kept, state.activeId);
  }
  return out;
};

const isThread = (value: unknown): value is ChatThread => {
  const t = value as ChatThread | null;
  return Boolean(
    t &&
      typeof t.id === 'string' &&
      typeof t.conversationId === 'string' &&
      Array.isArray(t.messages) &&
      Boolean(t.context)
  );
};

const read = (userKey: string): ThreadState | null => {
  const key = keyFor(userKey);
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;

  const drop = () => {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // Intentionally ignored — an unreadable blob is already being discarded.
    }
  };

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== VERSION || !parsed.threads || typeof parsed.threads !== 'object') {
      drop();
      return null;
    }
    const threads = Object.values(parsed.threads)
      .filter(isThread)
      .sort((a, b) => b.createdAt - a.createdAt);
    if (!threads.length) {
      drop();
      return null;
    }
    return rebuild(threads, typeof parsed.activeId === 'string' ? parsed.activeId : null);
  } catch {
    drop();
    return null;
  }
};

const write = (userKey: string, state: ThreadState): boolean => {
  try {
    window.sessionStorage.setItem(keyFor(userKey), JSON.stringify(prune(state)));
    return true;
  } catch {
    return false;
  }
};

export const conversationStore = {
  load: read,

  // False means storage refused the write (quota); the caller keeps its in-memory copy
  // and the threads simply will not survive a refresh.
  save: write,
};
